import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from '@mui/material';
import strings from '../../../../locales/ro.json';
import './ContactMessagePopup.css';

const ContactMessagePopup = ({ isOpen, template, templates, sampleName, onClose, onSend, sending }) => {
  const [variables, setVariables] = useState([]);
  const [values, setValues] = useState({});
  const [selectedTemplateKey, setSelectedTemplateKey] = useState('');
  const s = strings.admin.contacting.popup;

  const hasMultipleTemplates = templates && typeof templates === 'object';

  const templateOptions = useMemo(() => {
    if (!hasMultipleTemplates) return [];
    return Object.entries(templates).map(([key, val]) => ({
      key,
      label: val.label || key,
    }));
  }, [hasMultipleTemplates, templates]);

  const activeTemplate = useMemo(() => {
    if (hasMultipleTemplates) {
      if (!selectedTemplateKey || !templates[selectedTemplateKey]) return null;
      return templates[selectedTemplateKey].text;
    }
    return template;
  }, [hasMultipleTemplates, templates, selectedTemplateKey, template]);

  // Reset selected template key when popup opens or templates change
  useEffect(() => {
    if (isOpen && hasMultipleTemplates && templateOptions.length > 0) {
      setSelectedTemplateKey(templateOptions[0].key);
    } else if (isOpen && !hasMultipleTemplates) {
      setSelectedTemplateKey('');
    }
  }, [isOpen, hasMultipleTemplates, templateOptions]);

  useEffect(() => {
    if (!activeTemplate) {
      setVariables([]);
      setValues({});
      return;
    }
    const regex = /\[(.*?)\]/g;
    const matches = [...activeTemplate.matchAll(regex)];
    const extracted = new Set(matches.map(m => m[1]));
    
    extracted.delete("nume");
    extracted.delete("Nume");

    const varsArray = Array.from(extracted);
    setVariables(varsArray);

    const initialValues = {};
    varsArray.forEach(v => {
      initialValues[v] = values[v] || '';
    });
    setValues(initialValues);
  }, [activeTemplate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (varName, val) => {
    setValues(prev => ({ ...prev, [varName]: val }));
  };

  const previewText = useMemo(() => {
    if (!activeTemplate) return "";
    let text = activeTemplate.replace(/\[nume\]/gi, sampleName || "Nume_Exemplu");
    
    variables.forEach(v => {
      const regex = new RegExp(`\\[${v}\\]`, 'g');
      const replacement = values[v] ? values[v] : `[${v}]`;
      text = text.replace(regex, replacement);
    });
    return text;
  }, [activeTemplate, sampleName, variables, values]);

  const renderPreview = (text) => {
    return text.split('\n').map((line, i) => (
      <React.Fragment key={i}>
        {line}
        <br />
      </React.Fragment>
    ));
  };

  const handleSend = () => {
    if (onSend) {
      onSend(values, previewText);
    }
  };

  const isSendDisabled = useMemo(() => {
    if (sending) return true;
    if (!activeTemplate) return true;
    return variables.some(v => !values[v] || values[v].trim() === '');
  }, [variables, values, sending, activeTemplate]);

  return (
    <Modal open={isOpen} onClose={onClose}>
      <div className="cmp-overlay" onClick={onClose}>
        <div className="cmp-container" onClick={(e) => e.stopPropagation()}>
          <div className="cmp-header">
            <h2 className="cmp-title">{s.title}</h2>
            <button className="cmp-close-btn" onClick={onClose} disabled={sending}>✕</button>
          </div>
          
          <div className="cmp-content">
            <div className="cmp-form-section">
              {hasMultipleTemplates && (
                <div className="cmp-template-selector">
                  <h3 className="cmp-section-title">{s.templateLabel}</h3>
                  <div className="cmp-template-options">
                    {templateOptions.map(opt => (
                      <button
                        key={opt.key}
                        className={`cmp-template-btn ${selectedTemplateKey === opt.key ? 'active' : ''}`}
                        onClick={() => setSelectedTemplateKey(opt.key)}
                        disabled={sending}
                        type="button"
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <h3 className="cmp-section-title">{s.variablesLabel}</h3>
              {variables.length === 0 ? (
                <p className="cmp-no-vars">Nu există variabile manuale de completat.</p>
              ) : (
                <div className="cmp-inputs-grid">
                  {variables.map(v => (
                    <div className="cmp-input-group" key={v}>
                      <label className="cmp-label">{v.replace(/_/g, ' ')}</label>
                      <input
                        type="text"
                        className="cmp-input"
                        placeholder={`Introduceți ${v}`}
                        value={values[v] || ''}
                        onChange={(e) => handleChange(v, e.target.value)}
                        disabled={sending}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="cmp-preview-section">
              <h3 className="cmp-section-title">{s.previewLabel}</h3>
              <div className="cmp-preview-wrapper">
                <div className="cmp-preview-box">
                  {renderPreview(previewText)}
                </div>
              </div>
            </div>
          </div>

          <div className="cmp-footer">
            <button className="cmp-btn secondary" onClick={onClose} disabled={sending}>
              {s.cancelBtn}
            </button>
            <button 
              className={`cmp-btn ${isSendDisabled ? 'secondary' : 'primary'}`} 
              onClick={handleSend} 
              disabled={isSendDisabled}
            >
              {sending ? "Se trimite..." : s.sendBtn}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ContactMessagePopup;
